import {
  getApigeeUrl,
  getEnv,
  getRegion,
  ENVS,
  REGIONS,
} from '@adsk/acs-sc-web-platform-api';
import { SERVICE_ACCESSES } from '@adsk/acs-admin-ui-components-helpers';
import { WORKFLOWS_ROLES } from '../../constants';
import { getSelectValueLabel } from './utils';

const US_CONFIG = {
  [ENVS.QA]: `${getApigeeUrl()}/construction/rfis-qa`,
  [ENVS.STAGING]: `${getApigeeUrl()}/construction/rfis-stg`,
  [ENVS.PRODUCTION]: `${getApigeeUrl()}/construction/rfis`,
};

const ENV_CONFIGS = {
  [REGIONS.US]: US_CONFIG,
  [REGIONS.EMEA]: US_CONFIG,
  [REGIONS.APAC]: US_CONFIG,
};

export const buildGetRFIsPermissionsRequest = (projectId) => {
  const env = getEnv();
  const region = getRegion();

  return `${ENV_CONFIGS[region][env]}/v2/projects/${projectId}/workflow`;
};

const translateRoleValue = (T, role, workflowType) => {
  const name = role.replace('project', '').toLowerCase();
  return T.translate(
    `PERMISSIONS.RFIS.ROLES.${
      name === WORKFLOWS_ROLES.ARCHITECT ? `${name}.${workflowType}` : name
    }`
  );
};

const getRFIsSelectOptions = (T, projectRolesMapping, workflowType) =>
  (projectRolesMapping || []).map((role) => ({
    value: role,
    label: translateRoleValue(T, role, workflowType),
  }));

const getRFIsInheritanceInfo = (
  accessLevelPermission,
  companyPermissions,
  rolesPermissions,
  selectedCompanyName,
  workflowType,
  T
) => {
  const inheritanceInfo = [];

  if (accessLevelPermission) {
    const info = T.div({
      text: {
        key: 'INHERITED_PERMISSIONS_INFO_MULTIPLE',
        permission: T.translate('PERMISSIONS.RFIS.ROLES.gc'),
        subjectType: T.translate('PERMISSIONS.TYPES.ACCESS_LEVEL'),
        subjectName: 'Admin',
      },
      key: 'inheritedAccessLevelPermissions',
    });
    inheritanceInfo.push(info);
  }

  if (companyPermissions?.length > 0) {
    companyPermissions.forEach((companyPermission) => {
      const info = T.div({
        text: {
          key: 'INHERITED_PERMISSIONS_INFO_MULTIPLE',
          permission: translateRoleValue(T, companyPermission, workflowType),
          subjectType: T.translate('PERMISSIONS.TYPES.COMPANY_TEXT'),
          subjectName: selectedCompanyName,
        },
        key: 'inheritedCompanyPermissions',
      });
      inheritanceInfo.push(info);
    });
  }

  if (rolesPermissions?.length > 0) {
    rolesPermissions.forEach((rolesPermission) => {
      const info = T.div({
        text: {
          key: 'INHERITED_PERMISSIONS_INFO_MULTIPLE',
          permission: translateRoleValue(T, rolesPermission.name, workflowType),
          subjectType: T.translate('PERMISSIONS.TYPES.ROLE_TEXT'),
          subjectName: rolesPermission.roles[0].name,
        },
        key: 'inheritedRolePermissions',
      });
      inheritanceInfo.push(info);
    });
  }

  return inheritanceInfo;
};

export const getRFIsPermissionsInfo = (
  memberPermissions,
  companyPermissions,
  rolesPermissions,
  roles,
  workflowType,
  selectedAccessLevel,
  selectedCompany,
  T
) => {
  const selectOptions = getRFIsSelectOptions(T, roles, workflowType);
  const inheritedAccessLevelPermission =
    selectedAccessLevel === SERVICE_ACCESSES.ADMIN &&
    !memberPermissions.includes('projectGC')
      ? 'projectGC'
      : null;
  const inheritedCompanyPermissions =
    companyPermissions && companyPermissions.length > 0
      ? companyPermissions.filter(
          (companyPermission) =>
            !memberPermissions.includes(companyPermission) &&
            inheritedAccessLevelPermission !== companyPermission
        )
      : [];
  const inheritedRolePermissions =
    rolesPermissions.length > 0
      ? rolesPermissions.filter(
          (rolesPermission) =>
            !memberPermissions.some(
              (memberPermission) => rolesPermission.name === memberPermission
            ) &&
            inheritedAccessLevelPermission !== rolesPermission.name &&
            !inheritedCompanyPermissions.some(
              (companyPermission) => rolesPermission.name === companyPermission
            )
        )
      : [];
  const inheritedPermissionsList = [
    ...(inheritedAccessLevelPermission ? [inheritedAccessLevelPermission] : []),
    ...inheritedCompanyPermissions,
    ...inheritedRolePermissions.map((rolePermission) => rolePermission.name),
  ].map((inheritedPermission) => ({ value: inheritedPermission }));
  const selectValue = [
    ...(memberPermissions && memberPermissions.length > 0
      ? memberPermissions.map((memberPermission) => ({
          value: memberPermission,
        }))
      : []),
    ...inheritedPermissionsList,
  ];
  const inheritedPermissions = inheritedPermissionsList.length > 0;
  const inheritanceInfo = inheritedPermissions
    ? getRFIsInheritanceInfo(
        inheritedAccessLevelPermission,
        inheritedCompanyPermissions,
        inheritedRolePermissions,
        selectedCompany?.name,
        workflowType,
        T
      )
    : null;
  const inheritedPermissionsInfo = inheritedPermissions
    ? T.translate('INHERITED_PERMISSIONS')
    : null;

  return {
    selectValue,
    inheritedPermissionsInfo,
    inheritedPermissions,
    inheritanceInfo,
    selectOptions,
    multiple: true,
    selectValueLabel:
      selectValue.length > 0
        ? getSelectValueLabel(selectValue, selectOptions)
        : T.translate('PERMISSIONS.RFIS.NO_ACCESS'),
  };
};
